import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthToken = () => {
  const adminData = sessionStorage.getItem('adminkhadamat');
  if (!adminData) return null;

  try {
    const parsed = JSON.parse(adminData);
    if (parsed.data?.token?.access_token) return String(parsed.data.token.access_token);
    if (parsed.data?.access_token) return parsed.data.access_token;
    if (parsed.data?.token) return parsed.data.token;
    if (parsed.token) return parsed.token;
    if (parsed.result?.token) return parsed.result.token;
    if (parsed.user?.token) return parsed.user.token;
    return null;
  } catch {
    return null;
  }
};

export const fetchTechnicians = createAsyncThunk(
  'adminTechnicians/fetchTechnicians',
  async ({ page = 1, search = '' } = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const params = { page };
      if (search) params.search = search;

      const response = await axios.get(`${BASE_URL}/admin_technicans`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': 'ar',
          lang: 'ar',
        },
        params,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'فشل في جلب الفنيين');
    }
  }
);

export const addTechnician = createAsyncThunk(
  'adminTechnicians/addTechnician',
  async (formData, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(`${BASE_URL}/add_new_technicans`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': 'ar',
          lang: 'ar',
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      if (validationErrors) {
        const messages = Object.values(validationErrors).flat();
        return rejectWithValue(messages.join(' \n '));
      }

      return rejectWithValue(error.response?.data?.message || 'فشل في إضافة الفني');
    }
  }
);

export const updateTechnician = createAsyncThunk(
  'adminTechnicians/updateTechnician',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(`${BASE_URL}/update_technicans/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': 'ar',
          lang: 'ar',
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      if (validationErrors) {
        const messages = Object.values(validationErrors).flat();
        return rejectWithValue(messages.join(' \n '));
      }

      return rejectWithValue(error.response?.data?.message || 'فشل في تعديل الفني');
    }
  }
);

export const deleteTechnician = createAsyncThunk(
  'adminTechnicians/deleteTechnician',
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await axios.delete(`${BASE_URL}/delete_technicans/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': 'ar',
          lang: 'ar',
        },
      });

      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'فشل في حذف الفني');
    }
  }
);

const adminTechniciansSlice = createSlice({
  name: 'adminTechnicians',
  initialState: {
    technicians: [],
    pagination: null,
    loading: false,
    saving: false,
    deleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTechnicians.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTechnicians.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        const techniciansData =
          payload?.result?.data ||
          payload?.data?.data ||
          payload?.data ||
          [];
        state.technicians = Array.isArray(techniciansData) ? techniciansData : [];
        state.pagination = payload?.result?.pagination || payload?.data?.pagination || payload?.pagination || null;
      })
      .addCase(fetchTechnicians.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addTechnician.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addTechnician.fulfilled, (state, action) => {
        state.saving = false;
        state.success = action.payload?.message || 'تم إضافة الفني بنجاح';
      })
      .addCase(addTechnician.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateTechnician.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateTechnician.fulfilled, (state, action) => {
        state.saving = false;
        state.success = action.payload?.message || 'تم تعديل الفني بنجاح';
      })
      .addCase(updateTechnician.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteTechnician.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteTechnician.fulfilled, (state, action) => {
        state.deleting = false;
        const removedId = action.payload?.id;
        if (removedId) {
          state.technicians = state.technicians.filter((tech) => tech.id !== removedId);
        }
        state.success = action.payload?.data?.message || 'تم حذف الفني بنجاح';
      })
      .addCase(deleteTechnician.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = adminTechniciansSlice.actions;
export default adminTechniciansSlice.reducer;
